import { Button } from "@/components/ui/button";
import { TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Star, Trash2, Target, Users, Building2, ChartBar, Zap, BarChart2, Briefcase, MoreHorizontal, Trash, Sparkles } from "lucide-react";
import { Topic } from "../../../types/content";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TopicItemProps {
  topic: Topic;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onDelete: (id: string) => void;
  onGenerateContent: (id: string) => void;
}

// Helper function to get audience display name
const getAudienceDisplay = (audience: string) => {
  const audienceMap: Record<string, string> = {
    'technical-developers': 'Technical Developers',
    'marketing-managers': 'Marketing Managers',
    'startup-founders': 'Startup Founders',
    'product-managers': 'Product Managers',
    'business-owners': 'Business Owners',
    'data-scientists': 'Data Scientists',
  };
  return audienceMap[audience] || audience.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to get type display name and color
const getTypeInfo = (type: string) => {
  const typeMap: Record<string, { label: string, color: string }> = {
    'how-to': { label: 'How-To Guide', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/30' },
    'listicle': { label: 'Listicle', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800/30' },
    'tutorial': { label: 'Tutorial', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800/30' },
    'faq': { label: 'FAQ', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800/30' },
    'educational': { label: 'Educational', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800/30' },
    'checklist': { label: 'Checklist', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800/30' },
    'comparison': { label: 'Comparison', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800/30' },
    'trend': { label: 'Trend Analysis', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800/30' },
    'data': { label: 'Data Report', color: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700/50' },
  };
  return typeMap[type] || { label: type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
};

export const TopicItem = ({
  topic,
  isSelected,
  onSelect,
  onToggleFavorite,
  onDelete,
  onGenerateContent
}: TopicItemProps) => {
  if (!topic || !topic.id) {
    return null;
  }

  const typeInfo = getTypeInfo(topic.type);
  const audienceDisplay = getAudienceDisplay(topic.audience);

  return (
    <>
      <TableCell className="py-4 relative">
        <div className="flex items-center">
          <div className={cn(
            "font-medium line-clamp-2 group-hover:text-zinc-900 dark:group-hover:text-white transition-colors",
            isSelected ? "text-blue-700 dark:text-blue-400" : "text-zinc-800 dark:text-white"
          )}>
            {topic.title || 'Untitled Topic'}
          </div>
        </div>
      </TableCell>
      
      <TableCell>
        <div className="flex items-center">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400"></span>
            <span className="text-sm text-zinc-600 dark:text-zinc-300">{audienceDisplay}</span>
          </div>
        </div>
      </TableCell>
      
      <TableCell>
        <Badge variant="outline" className={cn("font-normal", typeInfo.color)}>
          {typeInfo.label}
        </Badge>
      </TableCell>
      
      <TableCell className="text-right">
        <div className="flex justify-end items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(topic.id);
                  }}
                  className={cn(
                    "h-8 w-8 rounded-full",
                    topic.isFavorite ? "text-yellow-500" : "text-zinc-300 dark:text-zinc-600"
                  )}
                >
                  <Star className={cn("h-5 w-5", topic.isFavorite ? "fill-yellow-500" : "")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{topic.isFavorite ? "Remove from favorites" : "Add to favorites"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-500 dark:text-zinc-400"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>More options</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end" className="dark:bg-zinc-800 dark:border-zinc-700">
              <DropdownMenuItem 
                className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 dark:focus:bg-zinc-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(topic.id);
                }}
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete topic
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onGenerateContent(topic.id);
            }}
            className="bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200 flex items-center gap-1.5 h-8 px-3 rounded-md shadow-sm hover:shadow"
          >
            <Sparkles className="h-4 w-4 mr-1 text-blue-500 dark:text-blue-400" />
            Generate
          </Button>
        </div>
      </TableCell>
    </>
  );
};
